// src/components/ProductSections.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Single import for both
import { getProducts, getFeaturedProducts } from '../services/api';
import './ProductSections.css';

// Modern Product Card Component for Featured and Top Sales
const ModernProductCard = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    alert(`${product.name} added to cart!`);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star filled">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="star half">½</span>);
    }
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="star empty">☆</span>);
    }
    return stars;
  };

  // Check if product is on sale
  const isOnSale = product.oldPrice && product.oldPrice > product.price;
  const discount = isOnSale ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;

  return (
    <div 
      className="modern-product-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Sale Badge */}
      {isOnSale && (
        <div className="sale-badge">
          <span>SALE</span>
        </div>
      )}
      
      {/* Product Image */}
      <Link to={`/product/${product.id}`} className="product-image-link">
        <div className="product-image-wrapper">
          <img 
            src={product.image || 'https://via.placeholder.com/400x400'} 
            alt={product.name}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x400?text=Product';
            }}
          />
        </div>
      </Link>
      
      {/* Product Info */}
      <div className="product-info-wrapper">
        <Link to={`/product/${product.id}`} className="product-title-link">
          <h3 className="modern-product-title">{product.name}</h3>
        </Link>
        
        {/* Rating Stars */}
        <div className="product-rating">
          {renderStars(product.rating || 0)}
          <span className="rating-count">({product.num_reviews || 0})</span>
        </div>
        
        {/* Price */}
        <div className="product-pricing">
          {isOnSale ? (
            <>
              <span className="original-price">${parseFloat(product.oldPrice).toFixed(2)}</span>
              <span className="sale-price">${parseFloat(product.price).toFixed(2)}</span>
            </>
          ) : (
            <span className="regular-price">${parseFloat(product.price).toFixed(2)}</span>
          )}
        </div>
      </div>
      
      {/* Add to Cart Button - Appears on hover with left-to-right animation */}
      <button 
        className={`add-to-cart-btn-modern ${isHovered ? 'visible' : ''}`}
        onClick={addToCart}
      >
        <span className="btn-text">Add to Cart</span>
        <span className="btn-icon">→</span>
      </button>
    </div>
  );
};

const ProductSections = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [topSalesProducts, setTopSalesProducts] = useState([]);
  const [loading, setLoading] = useState({
    featured: true,
    recent: true,
    topSales: true
  });

  useEffect(() => {
    loadFeaturedProducts();
    loadRecentProducts();
    loadTopSalesProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    setLoading(prev => ({ ...prev, featured: true }));
    try {
      const data = await getFeaturedProducts();
      if (data && data.success === true && data.products && data.products.length > 0) {
        setFeaturedProducts(data.products.slice(0, 4));
      } else {
        const allProducts = await getProducts();
        if (allProducts.success && allProducts.products) {
          setFeaturedProducts(allProducts.products.slice(0, 4));
        } else {
          setFeaturedProducts([]);
        }
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
      setFeaturedProducts([]);
    } finally {
      setLoading(prev => ({ ...prev, featured: false }));
    }
  };

  const loadRecentProducts = async () => {
    setLoading(prev => ({ ...prev, recent: true }));
    try {
      const data = await getProducts();
      if (data.success && data.products) {
        const recent = [...data.products].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setRecentProducts(recent);
      } else {
        setRecentProducts([]);
      }
    } catch (error) {
      console.error('Error loading recent products:', error);
      setRecentProducts([]);
    } finally {
      setLoading(prev => ({ ...prev, recent: false }));
    }
  };

  const loadTopSalesProducts = async () => {
    setLoading(prev => ({ ...prev, topSales: true }));
    try {
      const data = await getProducts();
      if (data.success && data.products) {
        const topSales = [...data.products].sort((a, b) => 
          (b.num_reviews || 0) - (a.num_reviews || 0) || (b.rating || 0) - (a.rating || 0)
        ).slice(0, 4);
        setTopSalesProducts(topSales);
      } else {
        setTopSalesProducts([]);
      }
    } catch (error) {
      console.error('Error loading top sales products:', error);
      setTopSalesProducts([]);
    } finally {
      setLoading(prev => ({ ...prev, topSales: false }));
    }
  };

  // Regular Section (for Featured and Top Sales)
  const RegularSection = ({ title, subtitle, products, loading, viewAllLink }) => (
    <div className="product-section regular-section">
      <div className="section-container">
        <div className="section-header">
          <div className="section-title-wrapper">
            <div>
              <h2>Featured <span>Products</span></h2>
              {subtitle && <p className="section-subtitle">{subtitle}</p>}
            </div>
          </div>
          <Link to={viewAllLink} className="view-all-link">
            View All <span>→</span>
          </Link>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="no-products">
            <p>No products available.</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <ModernProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Special Layout for Recent Products (1 large + 2 small)
  const RecentProductsSection = ({ title, subtitle, products, loading, viewAllLink }) => {
    if (loading) {
      return (
        <div className="product-section recent-section">
          <div className="section-container">
            <div className="section-header">
              <div className="section-title-wrapper">
                <div>
                  <h2>Recent <span>Products</span></h2>
                  <p className="section-subtitle">{subtitle}</p>
                </div>
              </div>
              <Link to={viewAllLink} className="view-all-link">
                View All <span>→</span>
              </Link>
            </div>
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          </div>
        </div>
      );
    }

    if (products.length === 0) {
      return (
        <div className="product-section recent-section">
          <div className="section-container">
            <div className="section-header">
              <div className="section-title-wrapper">
                <div>
                  <h2>Featured <span>Products</span></h2>
                  <p className="section-subtitle">{subtitle}</p>
                </div>
              </div>
              <Link to={viewAllLink} className="view-all-link">
                View All <span>→</span>
              </Link>
            </div>
            <div className="no-products">
              <p>No products available.</p>
            </div>
          </div>
        </div>
      );
    }

    const mainProduct = products[0];
    const subProducts = products.slice(1, 3);

    return (
      <div className="product-section recent-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-title-wrapper">
              <div>
                <h2>New <span>Arrivals</span></h2>
                <p className="section-subtitle">{subtitle}</p>
              </div>
            </div>
            <Link to={viewAllLink} className="view-all-link">
              View All <span>→</span>
            </Link>
          </div>

          <div className="recent-products-layout">
            {/* Large Product */}
            <div className="large-product-card">
              <Link to={`/product/${mainProduct.id}`} className="large-product-link">
                <div className="large-product-image">
                  <img 
                    src={mainProduct.image || 'https://via.placeholder.com/600x400'} 
                    alt={mainProduct.name}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/600x400?text=Product';
                    }}
                  />
                </div>
                <div className="large-product-info">
                  <h3>{mainProduct.name}</h3>
                  <p className="product-description">{mainProduct.description?.substring(0, 100)}...</p>
                  <div className="product-price">${parseFloat(mainProduct.price).toFixed(2)}</div>
                  <button className="shop-now-btn">Shop Now →</button>
                </div>
              </Link>
            </div>

            {/* Small Products */}
            <div className="small-products-grid">
              {subProducts.map((product, index) => (
                <div key={product.id} className="small-product-card">
                  <Link to={`/product/${product.id}`} className="small-product-link">
                    <div className="small-product-image">
                      <img 
                        src={product.image || 'https://via.placeholder.com/300x300'} 
                        alt={product.name}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x300?text=Product';
                        }}
                      />
                    </div>
                    <div className="small-product-info">
                      <h4>{product.name}</h4>
                      <div className="product-price">${parseFloat(product.price).toFixed(2)}</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="product-sections-wrapper">
      <RegularSection
        title="Featured Products"
        subtitle="Hand-picked just for you"
        products={featuredProducts}
        loading={loading.featured}
        viewAllLink="/shop?filter=featured"
      />

      <RecentProductsSection
        title="Recent Products"
        subtitle="New arrivals you'll love"
        products={recentProducts}
        loading={loading.recent}
        viewAllLink="/shop?sort=newest"
      />

      <RegularSection
        title="Top Sales"
        subtitle="Bestselling items this week"
        products={topSalesProducts}
        loading={loading.topSales}
        viewAllLink="/shop?sort=popular"
      />
    </div>
  );
};

export default ProductSections;